import React, { useState, useEffect, useRef } from "react";
import { buildApiUrl } from "../utils/apiConfig";
import { featuredVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import VideoLightbox from "./VideoLightbox";
import { decodeHtmlEntities } from "../utils/htmlUtils";
import "../styles/featured-content.css";
import "../styles/pagination.css";
import { apiClient } from "../utils/apiClient";

interface FeaturedContent {
    id: number;
    slug: string;
    title: string;
    featuredContentImageUrl: string;
    subtitle: string;
    linkLabel: string;
    linkUrl: string;
}

export default function ConnectFeaturedContentGrid() {
    const [allFeaturedContent, setAllFeaturedContent] = useState<FeaturedContent[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<{ id: number; title: string; vimeoUrl: string } | null>(null);
    const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({});
    const [imageErrorStates, setImageErrorStates] = useState<Record<number, boolean>>({});
    const contentRef = useRef<HTMLDivElement>(null);

    const ITEMS_PER_PAGE = 6;

    // Handler for page changes with smooth scroll
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        // Smooth scroll to top of featured content grid
        if (contentRef.current) {
            contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    useEffect(() => {
        const fetchFeaturedContent = async () => {
            try {
                setLoading(true);
                setError(null);

                const headers: HeadersInit = {
                    Accept: "application/json"
                };

                const response = await apiClient(
                    buildApiUrl(`/api/connect/featuredContent`),
                    {
                        headers,
                        credentials: "include" // Send HTTP-only cookies
                    }
                );

                if (!response.ok) {
                    throw new Error(`Failed to fetch featured content: ${response.status}`);
                }

                const data = await response.json();
                setAllFeaturedContent(data);

                // Initialize loading states for all images
                const initialLoadingStates: Record<number, boolean> = {};
                data.forEach((item: FeaturedContent) => {
                    initialLoadingStates[item.id] = false;
                });
                setImageLoadingStates(initialLoadingStates);
            } catch (err) {
                console.error("Error fetching featured content:", err);
                setError("Failed to load featured content. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchFeaturedContent();
    }, []);

    // Loading spinner
    if (loading) {
        return (
            <div className="featured-content-loading">
                <div className="featured-content-loading-spinner"></div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="featured-content-error">
                <svg
                    className="featured-content-error-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                </svg>
                <p className="featured-content-error-text">{error}</p>
            </div>
        );
    }

    // Empty state
    if (allFeaturedContent.length === 0) {
        return (
            <div className="featured-content-empty">
                <svg
                    className="featured-content-empty-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                </svg>
                <p className="featured-content-empty-text">No featured content available at this time.</p>
            </div>
        );
    }

    // Pagination logic
    const totalPages = Math.ceil(allFeaturedContent.length / ITEMS_PER_PAGE);
    const featuredContent = allFeaturedContent.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Helper function to check if a URL is a video URL
    const isVideoUrl = (url: string | null): boolean => {
        if (!url) return false;
        const videoPatterns = [
            /vimeo\.com/i,
            /wistia\.com/i,
            /youtube\.com/i,
            /youtu\.be/i,
            /\.mp4$/i,
            /\.webm$/i,
            /\.ogg$/i
        ];
        return videoPatterns.some(pattern => pattern.test(url));
    };

    // Helper function to check if a URL is a downloadable document
    const isDocumentUrl = (url: string | null): boolean => {
        if (!url) return false;
        const documentPatterns = [
            /\.pdf$/i,
            /\.doc$/i,
            /\.docx$/i,
            /\.xls$/i,
            /\.xlsx$/i,
            /\.ppt$/i,
            /\.pptx$/i,
            /\.csv$/i,
            /\.txt$/i,
            /\.zip$/i,
            /\.rar$/i
        ];
        return documentPatterns.some(pattern => pattern.test(url));
    };

    // Handler for button clicks
    const handleButtonClick = (item: FeaturedContent) => {
        if (!item.linkUrl) return;

        // If it's a video URL, open in lightbox
        if (isVideoUrl(item.linkUrl)) {
            setSelectedVideo({
                id: item.id,
                title: item.title,
                vimeoUrl: item.linkUrl
            });
        } else if (isDocumentUrl(item.linkUrl)) {
            // If it's a document, trigger download
            const link = document.createElement('a');
            link.href = item.linkUrl;
            link.download = '';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Otherwise, open URL in new tab
            window.open(item.linkUrl, '_blank', 'noopener,noreferrer');
        }
    };

    // Handler for image load
    const handleImageLoad = (itemId: number) => {
        setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
    };

    // Handler for image error
    const handleImageError = (itemId: number) => {
        setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
        setImageErrorStates(prev => ({ ...prev, [itemId]: true }));
    };

    return (
        <>
            <div className="featured-content-grid" ref={contentRef}>
                {featuredContent.map((item) => (
                    <div
                        key={item.id}
                        className="featured-content-card"
                        data-testid={`featured-content-${item.slug}`}
                    >
                        <div className="featured-content-card-body">
                            {/* Image and Content Container - Horizontal Layout */}
                            <div className="featured-content-header">
                                {/* Icon */}
                                <div className="featured-content-icon-wrapper">
                                    {imageLoadingStates[item.id] && !imageErrorStates[item.id] && (
                                        <div className="featured-content-icon-loading">
                                            <div className="featured-content-icon-spinner"></div>
                                        </div>
                                    )}
                                    <img
                                        src={imageErrorStates[item.id] ? "/api/placeholder/80/80" : item.featuredContentImageUrl}
                                        alt=""
                                        className="featured-content-icon"
                                        onLoad={() => handleImageLoad(item.id)}
                                        onError={() => handleImageError(item.id)}
                                    />
                                </div>

                                {/* Title and Subtitle */}
                                <div className="featured-content-text">
                                    {/* Subtitle Badge */}
                                    {item.subtitle && (
                                        <div className="featured-content-meta">
                                            <span className="featured-content-meta-badge">
                                                {item.subtitle}
                                            </span>
                                        </div>
                                    )}

                                    {/* Title */}
                                    <h3 className="featured-content-title">
                                        {decodeHtmlEntities(item.title)}
                                    </h3>
                                </div>
                            </div>

                            {/* CTA Button */}
                            <button
                                onClick={() => handleButtonClick(item)}
                                disabled={!item.linkUrl}
                                className={`featured-content-cta ${
                                    item.linkUrl
                                        ? "featured-content-cta-active"
                                        : "featured-content-cta-disabled"
                                }`}
                                aria-label={`${item.linkLabel} for ${item.title}`}
                                data-testid={`featured-content-action-${item.id}`}
                            >
                                {item.linkLabel}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
                <div className="pagination">
                    <div className="pagination-info">
                        <span>
                            Showing <strong>{(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, allFeaturedContent.length)}</strong> of <strong>{allFeaturedContent.length}</strong> resources
                        </span>
                    </div>

                    <div className="pagination-controls">
                        <div className="page-nav">
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                                disabled={currentPage <= 1}
                                aria-label="Previous page"
                            >
                                Previous
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    className={`page-number ${page === currentPage ? "page-number-active" : ""}`}
                                    onClick={() => handlePageChange(page)}
                                    aria-label={`Page ${page}`}
                                    aria-current={page === currentPage ? 'page' : undefined}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                                disabled={currentPage >= totalPages}
                                aria-label="Next page"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <VideoLightbox
                open={!!selectedVideo}
                resource={selectedVideo ?? undefined}
                onClose={() => setSelectedVideo(null)}
                analytics={selectedVideo ? featuredVideoAnalytics(WORDPRESS_TAB.CONNECT, selectedVideo.id) : null}
            />
        </>
    );
}
